/**
 * @description
 * Este arquivo cria diferentes instâncias do axios para cada um dos backends utilizados pelo painel.
 * Este arquivo também define os endpoints (rotas) dos diferentes backends utilizados pelo painel.
 *
 * As rotas descritas no arquivo `src/routes/paths.ts` são referentes aos "caminhos de URL" do frontend
 * (paths), que serão utilizados em outros arquivos para navegação via react-router.
 */

import axios, { AxiosRequestConfig, AxiosInstance, AxiosError } from 'axios';

import { APP_API_URL } from 'src/config-global';
import { STORAGE_KEY_ACCESS, STORAGE_KEY_REFRESH } from 'src/config/storage-keys';
import { captureRequestError } from 'src/init-sentry';

// Variável para controlar se já está renovando o token
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Factory function para criar instâncias axios padronizadas
const createAxiosInstance = (baseURL: string): AxiosInstance => {
  const instance = axios.create({ baseURL });

  instance.interceptors.request.use(
    (config) => {
      // Se for FormData, não sobrescreva o Content-Type
      // O browser vai setar automaticamente com o boundary correto
      if (!(config.data instanceof FormData)) {
        config.headers['Content-Type'] = 'application/json';
      }
      return { ...config };
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (res) => res,
    async (error: AxiosError) => {
      const originalRequest: any = error.config;

      // Se for erro 401 (token expirado/inválido) e não for a rota de refresh ou login
      if (
        (error.response?.status === 401 || error.response?.status === 403) &&
        originalRequest &&
        !originalRequest._retry &&
        !originalRequest.url?.includes('/auth/login') &&
        !originalRequest.url?.includes('/auth/refresh')
      ) {
        if (isRefreshing) {
          // Se já está renovando, adiciona na fila
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = localStorage.getItem(STORAGE_KEY_REFRESH);

        if (!refreshToken) {
          // Se não tem refresh token, desloga o usuário
          processQueue(error, null);
          isRefreshing = false;

          // Limpa a sessão e redireciona para login
          localStorage.removeItem(STORAGE_KEY_ACCESS);
          localStorage.removeItem(STORAGE_KEY_REFRESH);
          window.location.href = '/auth/jwt/login';

          return Promise.reject(error);
        }

        try {
          // Tenta renovar o token
          const response = await instance.post('/admin/auth/refresh', { refreshToken });
          const { accessToken, refreshToken: newRefreshToken } = response.data;

          // Salva os novos tokens
          localStorage.setItem(STORAGE_KEY_ACCESS, accessToken);
          if (newRefreshToken) {
            localStorage.setItem(STORAGE_KEY_REFRESH, newRefreshToken);
          }

          // Atualiza o header da instância
          instance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;

          // Dispara evento customizado para notificar que o token foi atualizado
          window.dispatchEvent(new CustomEvent('token-refreshed', { detail: { accessToken } }));

          // Processa a fila de requisições que falharam
          processQueue(null, accessToken);
          isRefreshing = false;

          // Refaz a requisição original
          return await instance(originalRequest);
        } catch (refreshError) {
          // Se falhar ao renovar, desloga o usuário
          processQueue(refreshError, null);
          isRefreshing = false;

          // Limpa a sessão e redireciona para login
          localStorage.removeItem(STORAGE_KEY_ACCESS);
          localStorage.removeItem(STORAGE_KEY_REFRESH);
          window.location.href = '/auth/jwt/login';

          return Promise.reject(refreshError);
        }
      }

      return Promise.reject((error.response && error.response.data) || 'Something went wrong');
    }
  );

  // Adicionar interceptor para capturar erros no Sentry
  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      // Capturar erro no Sentry
      captureRequestError({
        url: error.config?.url || 'unknown',
        method: error.config?.method?.toUpperCase() || 'UNKNOWN',
        status: error.response?.status,
        message: error.message,
        response: error.response?.data,
      });

      return Promise.reject(error);
    }
  );

  return instance;
};

export type axiosInstanceType = 'axiosInstance';

// Criar as instâncias
export const axiosInstance = createAxiosInstance(APP_API_URL);

// Factory function para criar fetchers
const createFetcher = (instance: AxiosInstance) => async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];
  const res = await instance.get(url, { ...config });
  return res.data;
};

// Criar os fetchers
export const appFetcher = createFetcher(axiosInstance);

// Endpoints da App API
export const endpoints = {
  motor: {
    rules: {
      getAll: '/motor/rules',
      getActive: '/motor/rules/active',
      create: '/motor/rules/create',
      update: '/motor/rules/update',
      inactivate: '/motor/rules/inactivate',
      delete: '/motor/rules/delete',
    },
    conversionRates: {
      getAll: '/motor/conversion-rates',
      getActive: '/motor/conversion-rates/active',
      create: '/motor/conversion-rates/create',
      update: '/motor/conversion-rates/update',
      inactivate: '/motor/conversion-rates/inactivate',
      activate: '/motor/conversion-rates/activate',
      delete: '/motor/conversion-rates/delete',
    },
    variables: '/variables',
    invoices: {
      getAll: '/invoices/all',
      review: '/invoices/review', // POST /invoices/review/:id
      getById: '/invoices', // GET /invoices/:id/unmasked
    },
    upload: {
      file: 'bucket/upload/values',
    },
  },
  admin: {
    adminLogs: '/adminlog',
    adminUser: {
      getAll: '/admin/admin-users',
      getById: '/admin/admin-user',
      create: '/admin/admin-user/create',
      update: '/admin/admin-user/update',
      inactivate: '/admin/admin-user/inactivate',
    },
    auth: {
      register: '/api/auth/register',
      login: '/admin/auth/login',
      refresh: '/admin/auth/refresh', // Refresh access token
    },
    whitelabel: '/admin/whitelabel',
  },
  shops: {
    getAll: '/shops',
  },
  establishments: {
    getAll: '/establishments',
    getById: '/establishments', // O ID será adicionado no repositório: /establishments/:id
    getByCNPJ: '/establishments/cnpj', // O CNPJ será adicionado no repositório: /establishments/cnpj/:cnpj
    search: '/establishments/search',
    delete: '/establishments/delete', // O ID será adicionado no repositório: /establishments/delete/:id
    activate: '/establishments/activate', // O ID será adicionado no repositório: /establishments/activate/:id
    inactivate: '/establishments/inactivate', // O ID será adicionado no repositório: /establishments/inactivate/:id
    uploadImage: '/establishments/upload-image', // Upload de imagem para o estabelecimento (POST)
    getBenefits: '/establishments/benefits', // O ID será adicionado no repositório: /establishments/benefits/:id
    getAllSimplified: '/establishments/simplified/all', // Lista estabelecimentos para vinculação em benefícios
    searchCnpjs: '/establishments/search/cnpjs', // GET /establishments/search/cnpjs?q=...&page=...&perPage=...
    store: {
      create: '/establishments/store/create',
      update: '/establishments/store/update', // O ID será adicionado no repositório: /establishments/store/update/:id
    },
    food: {
      create: '/establishments/food/create',
      update: '/establishments/food/update', // O ID será adicionado no repositório: /establishments/food/update/:id
    },
    service: {
      create: '/establishments/service/create',
      update: '/establishments/service/update', // O ID será adicionado no repositório: /establishments/service/update/:id
    },
    cinema: {
      create: '/establishments/cinema/create',
      update: '/establishments/cinema/update', // O ID será adicionado no repositório: /establishments/cinema/update/:id
    },
    categories: {
      getAll: '/establishments/categories',
      getPaginated: '/establishments/categories/paginated',
      create: '/establishments/categories/create',
      update: '/establishments/categories/update', // O ID será adicionado no repositório: /establishments/categories/update/:id
      delete: '/establishments/categories/delete', // O ID será adicionado no repositório: /establishments/categories/delete/:id
    },
  },
  appUsers: {
    getAll: '/app-users',
    search: '/app-users/search',
    getById: '/admin/app-users', // GET /admin/app-users/:userId
    getRedeems: '/admin/app-users', // GET /admin/app-users/:userId/redeems
    getInvoices: '/admin/app-users', // GET /admin/app-users/:userId/invoices
    update: '/admin/app-users/update', // PUT /admin/app-users/update/:userId
    getAllAppUserCampaigns: '/admin/app-users/campaigns', // GET /admin/app-users/campaigns?userId=...
  },
  terms: {
    getAll: '/terms',
    getLatest: '/terms/latest',
    getById: '/terms', // O ID será adicionado no repositório: /terms/:id
    create: '/terms/create',
    edit: '/terms/edit', // O ID será adicionado no repositório: /terms/edit/:id
    publish: '/terms/publish', // O ID será adicionado no repositório: /terms/publish/:id
    activate: '/terms/activate', // O ID será adicionado no repositório: /terms/activate/:id
    inactivate: '/terms/inactivate', // O ID será adicionado no repositório: /terms/inactivate/:id
  },
  policies: {
    getAll: '/policies',
    getLatest: '/policies/latest',
    getById: '/policies', // O ID será adicionado no repositório: /policies/:id
    create: '/policies/create',
    edit: '/policies/edit', // O ID será adicionado no repositório: /policies/edit/:id
    publish: '/policies/publish', // O ID será adicionado no repositório: /policies/publish/:id
    activate: '/policies/activate', // O ID será adicionado no repositório: /policies/activate/:id
    inactivate: '/policies/inactivate', // O ID será adicionado no repositório: /policies/inactivate/:id
  },
  faq: {
    items: {
      getAll: '/admin/faq-items',
      getById: '/admin/faq-items', // O ID será adicionado no repositório: /faq-items/:id
      getByCategory: '/admin/faq-items/category', // O ID será adicionado no repositório: /faq-items/category/:category-id
      create: 'admin/faq-items/create',
      update: '/admin/faq-items/update', // O ID será adicionado no repositório: /faq-items/update/:id
      activate: '/admin/faq-items/activate', // O ID será adicionado noRepositório: /faq-items/activate/:id
      inactivate: '/admin/faq-items/inactivate', // O ID será adicionado no repositório: /faq-items/inactivate/:id
    },
    categories: {
      getAll: '/admin/faq-categories',
      getById: '/admin/faq-categories', // O ID será adicionado no repositório: /faq-categories/:id
      simplified: '/admin/faq-categories/simplified',
      create: '/admin/faq-categories/create',
      update: '/admin/faq-categories/update', // O ID será adicionado no repositório: /faq-categories/update/:id
      activate: '/admin/faq-categories/activate', // O ID será adicionado no repositório: /faq-categories/activate/:id
      inactivate: '/admin/faq-categories/inactivate', // O ID será adicionado no repositório: /faq-categories/inactivate/:id
    },
  },
  shoppingSettings: {
    get: '/settings',
    update: '/settings/update',
    uploadLogo: '/settings/upload-logo',
  },
  benefits: {
    getAll: 'admin/benefits', // Lista todos os benefícios (Admin/User)
    search: 'admin/benefits/search', // Busca benefícios com termo 'q' e paginação (GET)
    getById: 'admin/benefits', // O ID será adicionado no repositório: /benefits/:id
    bulkActions: '/admin/benefits/bulk-actions', // Executa exclusão, ativação ou inativação em massa (POST)
    uploadImage: '/benefits/upload-image', // Upload de imagem para o benefício (POST)
    redeems: {
      getUserRedeems: '/benefits/redeems', // (User) Lista todos os resgates do usuário autenticado
      getAllRedeems: '/benefits/redeems/all', // (Admin) Lista todos os resgates
      getAllWithBenefits: '/benefits/redeems/with-benefits', // (Admin) Lista todos os resgates com detalhes dos benefícios
    },
    events: {
      create: '/events/create', // Cria evento (POST)
      update: '/events/update', // O ID será adicionado no repositório: /events/update/:id (PUT)
      delete: '/events/delete', // O ID será adicionado no repositório: /events/delete/:id (DELETE)
      activate: '/events/activate', // O ID será adicionado no repositório: /events/activate/:id (PATCH)
      inactivate: '/events/inactivate', // O ID será adicionado no repositório: /events/inactivate/:id (PATCH)
    },
    coupons: {
      create: '/coupons/create', // Cria cupom (POST)
      update: '/coupons/update', // O ID será adicionado no repositório: /coupons/update/:id (PUT)
      delete: '/coupons/delete', // O ID será adicionado no repositório: /coupons/delete/:id (DELETE)
      activate: '/coupons/activate', // O ID será adicionado no repositório: /coupons/activate/:id (PATCH)
      inactivate: '/coupons/inactivate', // O ID será adicionado no repositório: /coupons/inactivate/:id (PATCH)
      parkingPromotion: '/parking/promotions', // pega as informações das promoções de estacionamento
    },
    products: {
      create: '/products/create', // Cria produto (POST)
      update: '/products/update', // O ID será adicionado no repositório: /products/update/:id (PUT)
      delete: '/products/delete', // O ID será adicionado no repositório: /products/delete/:id (DELETE)
      activate: '/products/activate', // O ID será adicionado no repositório: /products/activate/:id (PATCH)
      inactivate: '/products/inactivate', // O ID será adicionado no repositório: /products/inactivate/:id (PATCH)
    },
  },
  userLevels: {
    getAll: '/user-levels',
    create: '/user-levels/create',
    update: '/user-levels/update', // O tier será adicionado no repositório: /user-levels/update/:tier
    delete: '/user-levels/delete', // O tier será adicionado no repositório: /user-levels/delete/:tier
  },
  webApp: {
    getAll: '/webapp/users',
    getById: '/webapp/users', // O ID será adicionado no repositório: /webapp/user/:id
    create: '/webapp/users/create',
    update: '/webapp/users/update', // O ID será adicionado no repositório: /webapp/user/update/:id
    delete: '/webapp/users/delete', // O ID será adicionado no repositório: /webapp/user/delete/:id
  },
  keepUp: {
    getAll: '/keep-ups',
    getById: '/keep-ups', // O ID será adicionado no repositório: /keep-ups/:id
    create: '/keep-ups/create',
    update: '/keep-ups/update', // O ID será adicionado no repositório: /keep-ups/update/:id
    uploadImage: '/keep-ups/upload-image', // Upload de imagem para o keep-up (POST)
    delete: 'keep-ups/delete',
  },
  campaign: {
    getAll: '/campaign', // get
    getActiveForLinking: '/notification/campaigns/active', // GET - Lista apenas campanhas ativas, para o seletor de vínculo de campanha em notificações
    getById: '/campaign/admin', // o id será adicionado no repositório /campaign/:id, com get
    create: '/campaign', // post
    update: '/campaign', // o id sera adicionado no repositorio /campaign/:id, com put
    delete: '/campaign',
    activate: '/campaign/activate',
    inactivate: '/campaign/inactivate',
    raffleCoupons: '/campaign/raffle-coupons', // o id sera adicionado no repositorio /campaign/raffle-coupons/:id, com get
  },
  roulette: {
    spins: {
      getAll: '/roulette/spins',
    },
    prizes: {
      getAll: '/roulette/prizes',
      getById: '/roulette/prize',
      create: '/roulette/prize/create',
      update: '/roulette/prize/update',
      activate: '/roulette/prize/activate',
      inactivate: '/roulette/prize/inactivate',
      delete: '/roulette/prize/delete',
    },
  },
  highlights: {
    getAll: '/highlights',
    getById: '/highlights', // O ID será adicionado no repositório: /highlights/:id
    create: '/highlights/create',
    update: '/highlights/update', // O ID será adicionado no repositório: /highlights/update/:id
    activate: '/highlights/activate', // O ID será adicionado no repositório: /highlights/activate/:id
    inactivate: '/highlights/inactivate', // O ID será adicionado no repositório: /highlights/inactivate/:id
    uploadImage: '/highlights/upload-image', // Upload de imagem para o highlight (POST)
    delete: 'highlights/delete',
  },
  stories: {
    getAll: '/stories',
    getById: '/stories', // O ID será adicionado no repositório: /stories/:id
    create: '/stories/create',
    update: '/stories/update', // O ID será adicionado no repositório: /stories/update/:id
    uploadMedia: '/stories/upload-media', // Upload de mídia (imagem ou vídeo) para o story (POST)
    delete: 'stories/soft-delete',
  },
  banners: {
    get: '/banners',
    update: '/banners/update',
  },
  home: {
    pages: {
      getAll: '/pages',
      getById: '/pages',
      getCurrent: '/pages/current',
      getByTier: '/pages/tier',
      create: '/pages/create',
      createFull: '/pages/create/full',
      update: '/pages/update',
      activate: '/pages/activate',
      inactivate: '/pages/deactivate',
      delete: '/pages/delete',
    },
    listings: {
      getAll: '/listings',
      getById: '/listings',
      create: '/listings/create',
      update: '/listings/update',
      delete: '/listings/delete',
    },
  },
  notifications: {
    campaign: {
      create: '/notification/campaign/create', // POST - Cria campanha de notificação
      getAll: '/notification/campaigns', // GET - Lista todas as campanhas
      getById: '/notification/campaign', // GET - Busca campanha por ID /:id
      update: '/notification/campaign/update', // PUT - Atualiza campanha /:id
      delete: '/notification/campaign/delete', // DELETE - Deleta campanha /:id
      estimate: '/notification/preview/estimate', // POST - Estima alcance da campanha
    },
    send: '/notification/send', // POST - Envia notificação para usuário específico
    getUserNotifications: '/notification/user', // GET - Lista notificações do usuário com paginação
    markAsViewed: '/notification', // PUT - Marca notificação como visualizada /:notification_id/view
    hasUnread: '/notification/has-unread', // GET - Verifica se há notificações não lidas
    logs: '/notification/all', // GET - Lista todos os logs de notificações enviadas (Admin)
  },
  proximityNotifications: {
    create: '/proximity/campaign',
    getAll: '/proximity/campaigns',
    getById: '/proximity/campaign',
    update: '/proximity/campaign',
    delete: '/proximity/campaign',
  },
  configs: {
    root: '/configs',
  },
  segments: {
    getAll: '/notification/segments', // GET - Lista todos os segmentos
    getById: '/notification/segment', // GET - Retorna um segmento por ID: /notification/segment/:id
    create: '/notification/segment/create', // POST - Cria segmento (não envia ID no payload)
    update: '/notification/segment/update', // PUT - Atualiza segmento: /notification/segment/update/:id
    delete: '/notification/segment/delete', // DELETE - Remove segmento (soft delete): /notification/segment/delete/:id
    preview: '/notification/segment/preview', // POST - Preview de segmento
    estimate: '/notification/segment/preview/estimate', // POST - Estima alcance do segmento
    sendPermanenceNotification: '/notification/segment/permanence-notification', // POST - Envia notificação de permanência: /notification/segment/permanence-notification/:id
  },
  missions: {
    getAll: '/missions/admin', // GET - Lista missões admin com paginação e filtros
    getById: '/missions/admin', // GET - Busca missão admin por ID: /missions/admin/:id
    create: '/missions/admin/create', // POST - Cria missão
    update: '/missions/admin/update', // PUT - Atualiza missão: /missions/admin/update/:id
    uploadImage: '/missions/admin/upload-image', // POST - Upload de imagem da missão
    activate: '/missions/admin/activate', // PATCH - Ativa missão: /missions/admin/activate/:id
    inactivate: '/missions/admin/inactivate', // PATCH - Inativa missão: /missions/admin/inactivate/:id
    delete: '/missions/admin/delete', // DELETE - Remove missão: /missions/admin/delete/:id
  },
  npsSearch: {
    uploadImage: 'admin/nps-search/upload-image',
    getNpsConfig: 'admin/nps-search/config',
    updateNps: 'admin/nps-search/config'
  },
  institutoForteTerms: {
    getActive: '/instituto-forte-terms/active', // GET (público)
    pendingTerms: '/instituto-forte-terms/pending-terms', // GET (user)
    accept: '/instituto-forte-terms/accept', // POST (user)
    create: '/instituto-forte-terms/create', // POST (admin) - já nasce "active", inativa o anterior automaticamente
    inactivate: '/instituto-forte-terms/inactivate', // POST (admin) - sem ID, sempre age sobre o termo ativo vigente
  },
}
